import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Bounce, toast } from 'react-toastify';
import dayjs from 'dayjs';
import PageHeader from '../../components/DashboardComponents/PageHeader';
import DashboardCard from '../../components/DashboardComponents/DashboardCard';
import MiniStat, { MiniStatRow } from '../../components/DashboardComponents/MiniStat';
import ProgressBar from '../../components/DashboardComponents/ProgressBar';
import { Avatar } from '../../components/DashboardComponents/AvatarGroup';
import ListingTabs from '../../components/Listing/ListingTabs';
import SearchInput from '../../components/Listing/SearchInput';
import FilterDropdown from '../../components/Listing/FilterDropdown';
import Pagination from '../../components/Listing/Pagination';
import CustomButton from '../../components/OnboardingComponents/CustomButton';
import CustomIconButton from '../../components/OnboardingComponents/CustomIconButton';
import EmptyState from '../../components/Common/EmptyState';
import TruncatedText from '../../components/Common/TruncatedText';
import DeleteConfirmation from '../../components/Common/DeleteConfirmation';
import { AddCollaboratorModal, AcceptInviteModal } from '../../components/Modals';
import { ICONS } from '../../constants/icons';
import { del, get, post } from '../../api/axios';
import endpoints from '../../api/endpoints';
import { toastApiError } from '../../helpers/apiError';
import { getUserAvatarUrl } from '../../helpers/userAvatar';
import { getXpProgress } from '../../helpers/xpLevel';
import { nameWithYou } from '../../helpers/userDisplay';

const PAGE_SIZE = 10;
const HISTORY_PAGE_SIZE = 10;
const SKELETON_ROWS = 6;
const HISTORY_SKELETON_ROWS = 4;
const DEFAULT_SORT_BY = 'created_at';
const DEFAULT_SORT_DIRECTION = 'desc';

const FILTER_TABS = [
  { key: 'all', label: 'All Invitations' },
  { key: 'sent_by_me', label: 'Sent by Me' },
  { key: 'pending_action', label: 'Pending Action' },
  { key: 'received', label: 'Received' },
  { key: 'accepted', label: 'Accepted' },
];

const SORT_OPTIONS = [
  { value: 'created_at', label: 'Created At' },
  { value: 'status', label: 'Status' },
  { value: 'role', label: 'Role' },
];

const STATUS_FILTER_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'active', label: 'Active' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'expired', label: 'Expired' },
];

const formatStatusLabel = (status) => {
  if (!status) return '—';
  const value = String(status);
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

const statusClass = (status) =>
  String(status || '')
    .toLowerCase()
    .replace(/\s/g, '-');

const formatDate = (value) => {
  if (!value) return '—';
  const date = dayjs(value);
  return date.isValid() ? date.format('MMMM D, YYYY') : '—';
};

const formatHistoryDate = (value) => {
  if (!value) return '—';
  const date = dayjs(value);
  return date.isValid() ? date.format('MMM D, YYYY') : '—';
};

const mapHistoryRow = (item = {}) => ({
  id: item.id,
  date: formatHistoryDate(item.date),
  status: formatStatusLabel(item.status),
  description: item.description || '—',
  amount: Number(item.amount) || 0,
});

const personName = (person = {}) =>
  person.full_name ||
  [person.first_name, person.last_name].filter(Boolean).join(' ').trim() ||
  person.email ||
  '';

const isExpiredByDate = (item) => {
  const expiresAt = item.invite?.expires_at || item.expires_at;
  if (!expiresAt) return false;
  const date = dayjs(expiresAt);
  return date.isValid() && date.isBefore(dayjs());
};

const mapInvite = (item, currentUserId) => {
  const inviter = item.inviter || {};
  const inviteRecord = item.invite;
  const inviteeUser = item.user;
  const hasUserId =
    item.user_id != null && String(item.user_id).trim() !== '';
  const invitedSource = !hasUserId
    ? inviteRecord || {}
    : inviteeUser || {};
  const invitedById = String(item.invited_by || inviter.id || '').trim();
  const currentId =
    currentUserId != null ? String(currentUserId).trim() : '';
  const inviteeUserId = String(item.user_id || '').trim();
  const isCurrentUserInvitee =
    Boolean(currentId) && hasUserId && inviteeUserId === currentId;

  const sentByMe = Boolean(currentId) && invitedById === currentId;

  const name =
    personName(invitedSource) ||
    invitedSource.email ||
    item.email ||
    'Unknown';
  const inviterName = personName(inviter) || inviter.email || 'Unknown';

  const project = item.project || {};
  const projectName = project.name || item.project_name || project.title || null;

  const statusRaw = String(
    item.status || inviteRecord?.status || ''
  ).toLowerCase();
  const expiredByDate = isExpiredByDate(item);
  const isAccepted = statusRaw === 'used' || statusRaw === 'active';
  const expired =
    !isAccepted &&
    (statusRaw === 'expired' ||
      String(inviteRecord?.status || '').toLowerCase() === 'expired' ||
      expiredByDate);
  const canRespond =
    isCurrentUserInvitee &&
    !expired &&
    (statusRaw === 'pending' || statusRaw === 'received');
  const canResend = !isCurrentUserInvitee && expired;
  const statusLabel = expired
    ? 'Expired'
    : canRespond
      ? 'Received'
      : isAccepted
        ? 'Accepted'
        : formatStatusLabel(item.status || inviteRecord?.status);

  return {
    id: item.id,
    name: nameWithYou(name, isCurrentUserInvitee),
    avatar: getUserAvatarUrl(invitedSource),
    userId: hasUserId ? item.user_id || inviteeUser?.id || null : null,
    inviterName: nameWithYou(inviterName, sentByMe),
    inviterAvatar: getUserAvatarUrl(inviter),
    inviterId: inviter.id || invitedById || null,
    messageUserId: isCurrentUserInvitee
      ? inviter.id || invitedById || null
      : item.user_id || inviteeUser?.id || null,
    projectName: projectName || '—',
    projectId: project.id || item.project_id || null,
    status: statusLabel,
    statusRaw: expired
      ? 'expired'
      : canRespond
        ? 'received'
        : statusRaw,
    invitedOn: formatDate(
      item.created_at || inviteRecord?.created_at || item.joined_at
    ),
    expiresOn: formatDate(inviteRecord?.expires_at || item.expires_at),
    sentByMe,
    canRespond,
    canResend,
    raw: item,
  };
};

const Invites = () => {
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.auth?.currentUser);
  const currentUserId = currentUser?.id;
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);
  const [sortDirection, setSortDirection] = useState(DEFAULT_SORT_DIRECTION);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [invites, setInvites] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invitesSummary, setInvitesSummary] = useState(null);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [acceptingInvite, setAcceptingInvite] = useState(null);
  const [rejectingInvite, setRejectingInvite] = useState(null);
  const [isRejecting, setIsRejecting] = useState(false);
  const [deletingInvite, setDeletingInvite] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(HISTORY_PAGE_SIZE);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [isHistoryLoading, setIsHistoryLoading] = useState(true);

  document.title = 'Invites | AMOK . EARTH';

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchInvitesSummary = useCallback(async () => {
    try {
      const response = await get(endpoints.getInvitesSummary);
      if (response?.data?.success) {
        setInvitesSummary(response.data.result || null);
      }
    } catch {
      // Keep last known summary; list errors surface separately.
    }
  }, []);

  useEffect(() => {
    fetchInvitesSummary();
  }, [fetchInvitesSummary]);

  const fetchBalanceHistory = useCallback(async () => {
    setIsHistoryLoading(true);

    try {
      const response = await get(endpoints.getInviteBalanceHistory, {
        params: {
          page: historyPage,
          page_size: HISTORY_PAGE_SIZE,
          per_page: HISTORY_PAGE_SIZE,
        },
      });

      if (response?.data?.success) {
        const result = response.data.result || {};
        const list = Array.isArray(result.data)
          ? result.data
          : Array.isArray(result)
            ? result
            : [];
        const rows = list.map(mapHistoryRow);
        const total = Number(result.total);
        const perPage =
          Number(result.per_page || result.page_size) || HISTORY_PAGE_SIZE;

        setHistoryRows(rows);
        setHistoryTotal(Number.isFinite(total) ? total : rows.length);
        setHistoryPageSize(perPage);

        const lastPage = Number(result.last_page);
        const totalPages = Math.max(
          1,
          Number.isFinite(lastPage) && lastPage > 0
            ? lastPage
            : Math.ceil(
                (Number.isFinite(total) ? total : rows.length) / perPage
              ) || 1
        );
        if (historyPage > totalPages) {
          setHistoryPage(totalPages);
        }
      } else if (response) {
        toastApiError(response, 'Failed to load invite balance history');
        setHistoryRows([]);
        setHistoryTotal(0);
      }
    } catch (err) {
      toastApiError(err, 'Failed to load invite balance history');
      setHistoryRows([]);
      setHistoryTotal(0);
    } finally {
      setIsHistoryLoading(false);
    }
  }, [historyPage]);

  useEffect(() => {
    fetchBalanceHistory();
  }, [fetchBalanceHistory]);

  const fetchInvites = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = {
        for_invite_tab: activeTab,
        page: currentPage,
        per_page: PAGE_SIZE,
        page_size: PAGE_SIZE,
        sort_by: sortBy,
        sort_direction: sortDirection,
        'with[]': ['inviter', 'invite', 'project', 'user'],
      };

      if (debouncedSearch) {
        params.search = debouncedSearch;
      }

      if (statusFilter) {
        params.status = statusFilter;
      }

      const response = await get(endpoints.getProjectInvitations, { params });

      if (response?.data?.success) {
        const result = response.data.result || {};
        const list = Array.isArray(result?.data)
          ? result.data
          : Array.isArray(result)
            ? result
            : [];
        const rows = list.map((item) => mapInvite(item, currentUserId));
        const total = Number(result.total);
        const perPage = Number(result.per_page || result.page_size) || PAGE_SIZE;

        setInvites(rows);
        setTotalItems(Number.isFinite(total) ? total : rows.length);
        setPageSize(perPage);

        const lastPage = Number(result.last_page);
        const totalPages = Math.max(
          1,
          Number.isFinite(lastPage) && lastPage > 0
            ? lastPage
            : Math.ceil((Number.isFinite(total) ? total : rows.length) / perPage) || 1
        );
        if (currentPage > totalPages) {
          setCurrentPage(totalPages);
        }
      } else {
        const message = toastApiError(response, 'Failed to load invites');
        setError(message);
        setInvites([]);
        setTotalItems(0);
      }
    } catch (err) {
      const message = toastApiError(err, 'Failed to load invites');
      setError(message);
      setInvites([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    activeTab,
    currentPage,
    currentUserId,
    debouncedSearch,
    sortBy,
    sortDirection,
    statusFilter,
  ]);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    setCurrentPage(1);
  };

  const remainingQuota = invitesSummary?.total_invites_quota_remaining;
  const inviteTopRight = invitesSummary?.has_unlimited_invites
    ? 'Unlimited'
    : remainingQuota ?? '—';
  const pendingInvites = invitesSummary?.total_pending ?? 0;
  const acceptedInvites = invitesSummary?.total_accepted ?? 0;
  const cancelledInvites = invitesSummary?.total_cancelled ?? 0;
  const { xpValue: progressValue, xpLevel: userLevel, xpMax: progressMax } =
    getXpProgress(currentUser);

  const openAcceptModal = (invite) => setAcceptingInvite(invite);

  const closeAcceptModal = () => setAcceptingInvite(null);

  const openRejectModal = (invite) => setRejectingInvite(invite);

  const closeRejectModal = () => {
    if (isRejecting) return;
    setRejectingInvite(null);
  };

  const openDeleteModal = (invite) => setDeletingInvite(invite);

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setDeletingInvite(null);
  };

  const handleRejectConfirm = async () => {
    if (!rejectingInvite?.id) return;

    setIsRejecting(true);
    try {
      const response = await post(
        endpoints.respondProjectInvitation(rejectingInvite.id),
        { action: 'reject' },
        { skipErrorToast: true }
      );

      if (!response?.data?.success) {
        toastApiError(
          response?.data || response,
          'Failed to reject invite'
        );
        return;
      }

      toast.success(
        response?.data?.message || 'Invite rejected successfully.',
        {
          position: 'bottom-center',
          theme: 'colored',
          transition: Bounce,
        }
      );
      setRejectingInvite(null);
      await Promise.all([
        fetchInvites(),
        fetchInvitesSummary(),
        fetchBalanceHistory(),
      ]);
    } catch (err) {
      toastApiError(err, 'Failed to reject invite');
    } finally {
      setIsRejecting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingInvite?.id) return;

    setIsDeleting(true);
    try {
      const response = await del(
        endpoints.deleteProjectInvitation(deletingInvite.id),
        { skipErrorToast: true }
      );

      if (response?.data?.success === false) {
        throw new Error(response?.data?.message || 'Failed to delete invite');
      }

      toast.success(
        response?.data?.message || 'Invite deleted successfully.',
        {
          position: 'bottom-center',
          theme: 'colored',
          transition: Bounce,
        }
      );
      setDeletingInvite(null);
      await Promise.all([
        fetchInvites(),
        fetchInvitesSummary(),
        fetchBalanceHistory(),
      ]);
    } catch (err) {
      toastApiError(err, 'Failed to delete invite');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleMessage = (invite) => {
    if (!invite.projectId || !invite.messageUserId) return;
    navigate(`/projects/${invite.projectId}/messages/${invite.messageUserId}`);
  };

  const renderRowActions = (invite) => {
    const status = String(invite.statusRaw || '').toLowerCase();
    const busy = isRejecting || isDeleting;

    if (invite.canRespond) {
      return (
        <div className="invites__row-actions">
          <button
            type="button"
            className="invites__action-pill invites__action-pill--accept"
            onClick={() => openAcceptModal(invite)}
            disabled={busy}
          >
            Accept
          </button>
          <button
            type="button"
            className="invites__action-pill invites__action-pill--reject"
            onClick={() => openRejectModal(invite)}
            disabled={busy}
          >
            Reject
          </button>
        </div>
      );
    }

    return (
      <div className="invites__row-actions">
        {invite.canResend ? (
          <CustomButton
            variant="secondary"
            size="sm"
            type="button"
            className="invites__resend"
            onClick={() => setIsInviteOpen(true)}
            disabled={busy}
          >
            Resend
          </CustomButton>
        ) : null}
        {status === 'accepted' || status === 'used' || status === 'active' ? (
          <CustomIconButton
            iconSrc={ICONS.messageTextBlue}
            label={`Message ${
              invite.messageUserId === invite.inviterId
                ? invite.inviterName
                : invite.name
            }`}
            size="sm"
            className="invites__message"
            onClick={() => handleMessage(invite)}
            disabled={
              !invite.messageUserId ||
              String(invite.messageUserId) === String(currentUserId) ||
              !invite.projectId ||
              isDeleting
            }
          />
        ) : null}
        <CustomIconButton
          iconSrc={ICONS.trash}
          label={`Delete invite for ${invite.name}`}
          tone="danger"
          size="sm"
          onClick={() => openDeleteModal(invite)}
          disabled={busy}
        />
      </div>
    );
  };

  return (
    <div className="page-content">
      <div className="invites">
        <PageHeader
          className="invites__header"
          title="Invites"
          subtitle="Manage your invites, view earn rates, and review pending collaborator requests."
          actions={
            <CustomButton
              variant="primary"
              size="md"
              type="button"
              trailingIconSrc={ICONS.plus}
              trailingIconAlt=""
              onClick={() => setIsInviteOpen(true)}
            >
              Invite Collaborator
            </CustomButton>
          }
        />

        <div className="invites__stats">
          <DashboardCard
            title="Invites Available"
            tone="darkblue"
            topRight={inviteTopRight}
          >
            <MiniStatRow>
              <MiniStat
                label="Pending"
                value={pendingInvites}
                color="darkblue"
              />
              <MiniStat
                label="Accepted"
                value={acceptedInvites}
                color="darkblue"
              />
              <MiniStat
                label="Declined"
                value={cancelledInvites}
                color="darkblue"
              />
            </MiniStatRow>
          </DashboardCard>

          <DashboardCard
            title="Progress to Next Level"
            tone="purple-custom"
            topRight={`Level ${userLevel}`}
          >
            <div className="invites__progress-body">
              <div className="invites__progress-row">
                <span className="invites__progress-value">{progressValue}xp</span>
                <span className="invites__progress-max">
                  / {progressMax}xp
                </span>
              </div>
              <ProgressBar
                value={progressValue}
                max={Math.max(progressMax, 1)}
                tone="purple"
                className="invites__progress-bar"
              />
            </div>
          </DashboardCard>
        </div>

        <div className="projects-listing__toolbar">
          <ListingTabs
            items={FILTER_TABS}
            activeKey={activeTab}
            onChange={handleTabChange}
          />
          <div className="projects-listing__controls">
            <SearchInput
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search by Title..."
              iconSrc={ICONS.callFormalSearchStatus}
            />
            <FilterDropdown
              label="Filter"
              filters={[
                {
                  key: 'status',
                  type: 'select',
                  label: 'Status',
                  multiple: false,
                  options: STATUS_FILTER_OPTIONS,
                  value: statusFilter,
                  onChange: (next) => {
                    setStatusFilter(
                      Array.isArray(next) ? next[0] || '' : next || ''
                    );
                    setCurrentPage(1);
                  },
                },
                {
                  key: 'sortBy',
                  type: 'sortBy',
                  label: 'Sort By',
                  value: sortBy,
                  defaultValue: DEFAULT_SORT_BY,
                  onChange: (next) => {
                    setSortBy(next || DEFAULT_SORT_BY);
                    setCurrentPage(1);
                  },
                  options: SORT_OPTIONS,
                },
                {
                  key: 'sortDirection',
                  type: 'sortDirection',
                  label: 'Direction',
                  value: sortDirection,
                  defaultValue: DEFAULT_SORT_DIRECTION,
                  onChange: (next) => {
                    setSortDirection(next || DEFAULT_SORT_DIRECTION);
                    setCurrentPage(1);
                  },
                },
              ]}
            />
          </div>
        </div>

        <div className="projects-listing__table-wrapper">
          <div
            className={`projects-listing__table invites__table${
              activeTab === 'sent_by_me' ? ' invites__table--sent' : ''
            }`}
            aria-busy={isLoading || undefined}
          >
            <div className="projects-listing__table-row projects-listing__table-row--head">
              <div>Invited To</div>
              {activeTab !== 'sent_by_me' ? <div>Invited By</div> : null}
              <div>Project</div>
              <div>Status</div>
              <div>Invited On</div>
              <div>Expires On</div>
              <div className="projects-listing__table-actions-head">Action</div>
            </div>

            {isLoading &&
              Array.from({ length: SKELETON_ROWS }).map((_, index) => (
                <div
                  key={`skeleton-${index}`}
                  className="projects-listing__table-row projects-listing__table-row--skeleton"
                  aria-hidden="true"
                >
                  {Array.from({
                    length: activeTab === 'sent_by_me' ? 6 : 7,
                  }).map((__, cellIdx) => (
                    <div key={cellIdx} className="projects-listing__skeleton-cell">
                      <div className="projects-listing__skeleton-bar" />
                    </div>
                  ))}
                </div>
              ))}

            {!isLoading &&
              invites.map((invite) => (
                <div key={invite.id} className="projects-listing__table-row">
                  <div className="projects-listing__table-cell projects-listing__table-cell--title">
                    <Avatar
                      src={invite.avatar}
                      name={invite.name}
                      size="sm"
                      className="invites__avatar"
                    />
                    <TruncatedText className="projects-listing__table-title">
                      {invite.name}
                    </TruncatedText>
                  </div>
                  {activeTab !== 'sent_by_me' ? (
                    <div className="projects-listing__table-cell projects-listing__table-cell--title">
                      <Avatar
                        src={invite.inviterAvatar}
                        name={invite.inviterName}
                        size="sm"
                        className="invites__avatar"
                      />
                      <TruncatedText className="projects-listing__table-title">
                        {invite.inviterName}
                      </TruncatedText>
                    </div>
                  ) : null}
                  <div className="projects-listing__table-cell">
                    <TruncatedText>{invite.projectName}</TruncatedText>
                  </div>
                  <div className="projects-listing__table-cell">
                    <span
                      className={`invites__status invites__status--${statusClass(
                        invite.status
                      )}`}
                    >
                      {invite.status}
                    </span>
                  </div>
                  <div className="projects-listing__table-cell">
                    <TruncatedText>{invite.invitedOn}</TruncatedText>
                  </div>
                  <div className="projects-listing__table-cell">
                    <TruncatedText>{invite.expiresOn}</TruncatedText>
                  </div>
                  <div className="projects-listing__table-cell projects-listing__table-cell--actions">
                    {renderRowActions(invite)}
                  </div>
                </div>
              ))}

            {!isLoading && error && invites.length === 0 && (
              <EmptyState
                title="Unable to load invites"
                description={error}
              />
            )}

            {!isLoading && !error && invites.length === 0 && (
              <EmptyState
                title="No invitations found"
                description="Try adjusting your search or filters."
              />
            )}
          </div>
        </div>

        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          itemLabel="Invitations"
          prevIconSrc={ICONS.chevronLeft}
          nextIconSrc={ICONS.chevronRight}
        />

        <section className="invites__history">
          <h2 className="invites__history-title">Invite Balance History</h2>
          <div
            className="projects-listing__table invites__table invites__table--history"
            aria-busy={isHistoryLoading || undefined}
          >
            <div className="projects-listing__table-row projects-listing__table-row--head">
              <div>Date</div>
              <div>Status</div>
              <div>Description</div>
              <div>Amount</div>
            </div>
            {isHistoryLoading &&
              Array.from({ length: HISTORY_SKELETON_ROWS }).map((_, index) => (
                <div
                  key={`history-skeleton-${index}`}
                  className="projects-listing__table-row projects-listing__table-row--skeleton"
                  aria-hidden="true"
                >
                  {Array.from({ length: 4 }).map((__, cellIdx) => (
                    <div
                      key={cellIdx}
                      className="projects-listing__skeleton-cell"
                    >
                      <div className="projects-listing__skeleton-bar" />
                    </div>
                  ))}
                </div>
              ))}
            {!isHistoryLoading &&
              historyRows.map((row) => (
                <div key={row.id} className="projects-listing__table-row">
                  <div className="projects-listing__table-cell">
                    <TruncatedText>{row.date}</TruncatedText>
                  </div>
                  <div className="projects-listing__table-cell">
                    <span
                      className={`invites__history-status invites__history-status--${statusClass(
                        row.status
                      )}`}
                    >
                      {row.status}
                    </span>
                  </div>
                  <div className="projects-listing__table-cell projects-listing__table-cell--title">
                    <TruncatedText className="projects-listing__table-title">
                      {row.description}
                    </TruncatedText>
                  </div>
                  <div
                    className={`projects-listing__table-cell invites__amount ${
                      row.amount < 0
                        ? 'invites__amount--used'
                        : 'invites__amount--earned'
                    }`}
                  >
                    {row.amount > 0 ? `+${row.amount}` : row.amount}
                  </div>
                </div>
              ))}
            {!isHistoryLoading && historyRows.length === 0 && (
              <EmptyState title="No invite balance history yet" />
            )}
          </div>
          <Pagination
            currentPage={historyPage}
            totalItems={historyTotal}
            pageSize={historyPageSize}
            onPageChange={setHistoryPage}
            itemLabel="Entries"
            prevIconSrc={ICONS.chevronLeft}
            nextIconSrc={ICONS.chevronRight}
          />
        </section>

        <AddCollaboratorModal
          isOpen={isInviteOpen}
          onClose={() => setIsInviteOpen(false)}
          showProjectSelect
          title="Invite Collaborator"
          subtitle="Select a project and invite collaborators to join."
          onInvited={() =>
            Promise.all([
              fetchInvites(),
              fetchInvitesSummary(),
              fetchBalanceHistory(),
            ])
          }
        />

        <AcceptInviteModal
          isOpen={Boolean(acceptingInvite)}
          onClose={closeAcceptModal}
          invite={acceptingInvite}
          onSuccess={() =>
            Promise.all([
              fetchInvites(),
              fetchInvitesSummary(),
              fetchBalanceHistory(),
            ])
          }
        />

        <DeleteConfirmation
          isOpen={Boolean(rejectingInvite)}
          onCancel={closeRejectModal}
          onConfirm={handleRejectConfirm}
          title="Reject Invite"
          message="Are you sure you want to reject this invitation? This action cannot be undone."
          cancelText="Cancel"
          confirmText="Reject Invite"
          isDeleting={isRejecting}
        />

        <DeleteConfirmation
          isOpen={Boolean(deletingInvite)}
          onCancel={closeDeleteModal}
          onConfirm={handleDeleteConfirm}
          title="Delete Invite"
          message={`Are you sure you want to delete the invite for ${
            deletingInvite?.name || 'this collaborator'
          }? This action cannot be undone.`}
          cancelText="Cancel"
          confirmText="Delete Invite"
          isDeleting={isDeleting}
        />
      </div>
    </div>
  );
};

export default Invites;
